import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { BrandMark } from '../components/BrandMark';
import { ChatWidget } from '../chat/ChatWidget';
import { ConsentBanner } from '../components/ConsentBanner';
import { DemoForm } from '../components/DemoForm';
import { LangSwitcher } from '../components/LangSwitcher';
import { PhoneMenu, KdsBoard, LoyaltyCard, QueueCard, TablesGrid, FEATURE_ICONS } from '../components/MockVisuals';
import { applySeo, LOCALE_PATHS, GUIDE_PATHS } from '../lib/seo';
import { startAnalytics, pageview, track } from '../lib/analytics';

interface Section { h: string; p: string }
interface Qa { q: string; a: string }
interface Feat { title: string; desc: string }

const LANGS = [
  { code: 'pt-BR', label: 'PT' },
  { code: 'en', label: 'EN' },
  { code: 'es', label: 'ES' },
  { code: 'fr', label: 'FR' }
];

export function GuidePage({ lang }: { lang: string }) {
  const { t, i18n } = useTranslation();

  useEffect(() => { if (i18n.resolvedLanguage !== lang) void i18n.changeLanguage(lang); }, [lang, i18n]);

  useEffect(() => {
    const faq = t('guide.faq', { returnObjects: true }) as Qa[];
    applySeo({
      lang,
      title: t('guide.seo_title'),
      description: t('guide.seo_description'),
      paths: GUIDE_PATHS,
      ld: {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faq.map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } }))
      }
    });
    startAnalytics();
    pageview(GUIDE_PATHS[lang] ?? '/sistema-para-restaurantes', t('guide.seo_title'));
  }, [lang, t]);

  const sections = t('guide.sections', { returnObjects: true }) as Section[];
  const faq = t('guide.faq', { returnObjects: true }) as Qa[];
  const features = t('marketing.features', { returnObjects: true }) as Feat[];

  // mockups intercalados entre as seções (índice → bloco visual)
  const bands: Record<number, { visual: JSX.Element; feat?: Feat }> = {
    3: { visual: <QueueCard />, feat: features[0] },
    8: { visual: <TablesGrid />, feat: features[1] },
    13: { visual: <KdsBoard />, feat: features[3] }
  };

  return (
    <div className="mkt">
      <header className="mkt-header">
        <a className="brand" href={LOCALE_PATHS[lang] ?? '/'}>
          <BrandMark className="brand__mark" />
          <span className="brand__name">La <em>petite</em> <span className="brand__accent">table</span></span>
        </a>
        <div className="mkt-header__right">
          <LangSwitcher lang={lang} paths={GUIDE_PATHS} />
          <Link to="/login" className="mkt-login">{t('marketing.nav.login')}</Link>
          <a href="#demo" className="btn btn--primary btn--sm" onClick={() => track('demo_click', { from: 'guide_nav' })}>{t('demo.title')}</a>
        </div>
      </header>

      <article className="guide">
        <h1 className="guide__h1">{t('guide.h1')}</h1>
        <p className="guide__intro">{t('guide.intro')}</p>

        <div className="guide-showcase">
          <div className="guide-shot"><PhoneMenu /><span>{features[2]?.title}</span></div>
          <div className="guide-shot"><KdsBoard /><span>{features[3]?.title}</span></div>
          <div className="guide-shot"><LoyaltyCard /><span>{features[4]?.title}</span></div>
        </div>

        <div className="guide-feat">
          {features.map((f, i) => (
            <div key={i} className="guide-feat__card">
              <span className="guide-feat__icon">{FEATURE_ICONS[i]}</span>
              <div><h3>{f.title}</h3><p>{f.desc}</p></div>
            </div>
          ))}
        </div>

        <nav className="guide__toc">
          <strong>{t('guide.toc_title')}</strong>
          <ol>
            {sections.map((s, i) => <li key={i}><a href={`#s${i}`}>{s.h}</a></li>)}
          </ol>
        </nav>

        {sections.map((s, i) => (
          <div key={i}>
            <section id={`s${i}`} className="guide__sec">
              <h2>{s.h}</h2>
              <p>{s.p}</p>
            </section>
            {bands[i] && (
              <div className="guide-band">
                <div className="guide-band__text">
                  <h3>{bands[i].feat?.title}</h3>
                  <p>{bands[i].feat?.desc}</p>
                </div>
                <div className="guide-band__visual">{bands[i].visual}</div>
              </div>
            )}
          </div>
        ))}

        <section className="guide__sec">
          <h2>{t('guide.faq_title')}</h2>
          {faq.map((item, i) => (
            <details key={i} className="mkt-faq__item">
              <summary>{item.q}</summary>
              <p>{item.a}</p>
            </details>
          ))}
        </section>

        <section className="guide__cta">
          <h2>{t('guide.cta_title')}</h2>
          <a href="#demo" className="btn btn--primary" onClick={() => track('demo_click', { from: 'guide_cta' })}>{t('guide.cta')}</a>
        </section>
      </article>

      <DemoForm lang={lang} />

      <footer className="mkt-footer">
        <div className="brand"><BrandMark className="brand__mark" /><span className="brand__name">La <em>petite</em> <span className="brand__accent">table</span></span></div>
        <div className="mkt-footer__links">
          <Link to={LOCALE_PATHS[lang] ?? '/'}>{t('dashboard.home')}</Link>
          {LANGS.map((l) => (<Link key={l.code} to={GUIDE_PATHS[l.code]}>{l.label}</Link>))}
        </div>
        <p className="mkt-footer__rights">© {t('app.name')} · {t('marketing.footer_rights')}</p>
      </footer>

      <ChatWidget lang={lang} />
      <ConsentBanner />
    </div>
  );
}
